import type { Dispatch, SetStateAction } from 'react';
import { Button, Dialog, Input, Select } from '@/design-system';
import type { CreateCouponData } from '../api/coupons.service';
import { formatCentsToBrl, parseBrlInputToCents } from '../utils/coupon-format';
import { DURATION_TYPE_LABELS, TRIAL_DAYS_OPTIONS } from '../utils/coupon-form';
import { useAffiliateProgramQuery } from '@/features/settings';

interface CouponFormDialogProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: CreateCouponData;
  formErrors: Record<string, string>;
  isSubmitting: boolean;
  onClose: () => void;
  onChange: Dispatch<SetStateAction<CreateCouponData>>;
  onSave: () => void;
}

export function CouponFormDialog({
  isOpen,
  mode,
  formData,
  formErrors,
  isSubmitting,
  onClose,
  onChange,
  onSave,
}: CouponFormDialogProps) {
  const program = useAffiliateProgramQuery(isOpen);
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? 'Editar cupom' : 'Novo cupom'}
      size="md"
      footer={(
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
          <Button variant="primary" onClick={onSave} isLoading={isSubmitting}>
            {mode === 'edit' ? 'Salvar' : 'Criar'}
          </Button>
        </div>
      )}
    >
      <div className="space-y-4">
        <Input
          label="Código do cupom"
          value={formData.code}
          onChange={(e) => onChange((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
          error={formErrors.code}
          placeholder="Ex: JOAO10, PARCEIRO30"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de benefício</label>
          <Select
            value={formData.discountStrategy}
            onChange={(e) => {
              const v = e.target.value as CreateCouponData['discountStrategy'];
              onChange((prev) => ({
                ...prev,
                discountStrategy: v,
                value: v === 'TRIAL_DAYS' ? 30 : v === 'PERCENTAGE' ? 10 : 5000,
              }));
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="TRIAL_DAYS">Dias grátis (período de teste)</option>
            <option value="PERCENTAGE">Desconto em porcentagem</option>
            <option value="FIXED_AMOUNT">Desconto em valor fixo (R$)</option>
          </Select>
        </div>
        {formData.discountStrategy === 'TRIAL_DAYS' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Período de teste</label>
            <Select
              value={formData.value}
              onChange={(e) =>
                onChange((prev) => ({ ...prev, value: parseInt(e.target.value, 10) as 7 | 15 | 30 }))
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {TRIAL_DAYS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
        )}
        {formData.discountStrategy === 'PERCENTAGE' && (
          <Input
            label="Porcentagem de desconto"
            type="number"
            min={0}
            max={100}
            value={String(formData.value)}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              onChange((prev) => ({
                ...prev,
                value: Number.isNaN(v) ? 0 : Math.min(100, Math.max(0, v)),
              }));
            }}
            error={formErrors.value}
            placeholder="0 a 100"
          />
        )}
        {formData.discountStrategy === 'FIXED_AMOUNT' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor do desconto (R$)</label>
            <Input
              type="text"
              inputMode="numeric"
              value={formData.value === 0 ? '' : `R$ ${formatCentsToBrl(formData.value)}`}
              onChange={(e) => onChange((prev) => ({ ...prev, value: parseBrlInputToCents(e.target.value) }))}
              placeholder="R$ 0,00"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                formErrors.value ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {formErrors.value && <p className="mt-1 text-sm text-red-600">{formErrors.value}</p>}
          </div>
        )}
        {(formData.discountStrategy === 'PERCENTAGE' || formData.discountStrategy === 'FIXED_AMOUNT') && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Duração do desconto</label>
              <Select
                value={formData.durationType}
                onChange={(e) =>
                  onChange((prev) => ({
                    ...prev,
                    durationType: e.target.value as CreateCouponData['durationType'],
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="ONCE">{DURATION_TYPE_LABELS.ONCE}</option>
                <option value="REPEATING">{DURATION_TYPE_LABELS.REPEATING}</option>
                <option value="FOREVER">{DURATION_TYPE_LABELS.FOREVER}</option>
              </Select>
            </div>
            {formData.durationType === 'REPEATING' && (
              <Input
                label="Quantidade de meses"
                type="number"
                min={1}
                max={120}
                value={String(formData.durationMonths ?? 3)}
                onChange={(e) =>
                  onChange((prev) => ({ ...prev, durationMonths: parseInt(e.target.value, 10) || 1 }))
                }
              />
            )}
          </>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Finalidade do cupom</label>
          <Select
            value={formData.usageType}
            onChange={(e) =>
              onChange((prev) => ({ ...prev, usageType: e.target.value as CreateCouponData['usageType'],
                affiliateId: e.target.value === 'AFFILIATE' ? prev.affiliateId : null }))
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="CAMPAIGN">Campanha (com limite e validade)</option>
            <option value="INTERNAL">Retenção (uso do suporte)</option>
            <option value="AFFILIATE" disabled={!program.data?.enabled}>Afiliado (vinculado a parceiro)</option>
          </Select>
        </div>
        <Input
          label="Descrição (opcional)"
          value={formData.description || ''}
          onChange={(e) => onChange((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Ex: 10% para sempre — cupom afiliado João"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Máximo de resgates (opcional)</label>
          <Input
            type="number"
            min={0}
            value={formData.maxRedemptions ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              onChange((prev) => ({ ...prev, maxRedemptions: v === '' ? undefined : parseInt(v, 10) || 0 }));
            }}
            placeholder="Ilimitado"
          />
        </div>
      </div>
    </Dialog>
  );
}
